//@ts-nocheck
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAlert } from 'react-alert';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import PageTemplate from 'PageTemplates/PageTemplate';
import { PageHeader } from 'components/header';
import { setSpinner } from 'services/store/actions/view';
import { getGlobalSettings } from 'services/store/actions/settings';
import { setStoragePageKey } from 'services/store/actions/view';
import {
  getStorageItems,
  createStorageOrder,
} from 'services/apiRequests/storage.api';
import ProducerSelect from './components/ProducerSelect';
import ProductsTable from './components/ProductsTable';
import OrderSummary from './components/OrderSummary';
import { validateOrderData } from './utils/orderUtils';

const StorageNewOrder = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const alert = useAlert();
  const mdfProducers = useSelector(
    (state) => state.settings.mdfProducers || [],
  );

  const [selectedProducer, setSelectedProducer] = useState('');
  const [availableProducts, setAvailableProducts] = useState([]);
  const [orderItems, setOrderItems] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Pobierz ustawienia globalne
    dispatch(setSpinner(true));
    dispatch(
      getGlobalSettings(
        () => {
          dispatch(setSpinner(false));
        },
        () => {
          dispatch(setSpinner(false));
          alert.error('Błąd pobierania ustawień!');
        },
      ),
    );
  }, [dispatch, alert]);

  const handleProducerChange = async (producerName) => {
    setSelectedProducer(producerName);
    setOrderItems({}); // Reset order items

    if (!producerName) {
      setAvailableProducts([]);
      return;
    }

    try {
      dispatch(setSpinner(true));
      const response = await getStorageItems();

      // Filtruj produkty które mają wybranego producenta
      const filteredProducts = response.items.filter((product) =>
        product.producers.some(
          (producer) => producer.name === producerName,
        ),
      );

      setAvailableProducts(filteredProducts);

      // Automatycznie ustaw sugerowane ilości jako domyślne
      const initialOrderItems = {};
      filteredProducts.forEach((product) => {
        const currentQuantity = product.quantity || 0;
        const desiredQuantity = product.desiredQuantity || 0;
        const suggestedQuantity = Math.max(
          0,
          desiredQuantity - currentQuantity,
        );

        if (suggestedQuantity > 0) {
          initialOrderItems[product._id] = suggestedQuantity.toString();
        }
      });

      setOrderItems(initialOrderItems);
    } catch (error) {
      alert.error('Błąd pobierania produktów');
      console.error('Error fetching products:', error);
    } finally {
      dispatch(setSpinner(false));
    }
  };

  const handleQuantityChange = (productId, quantity) => {
    setOrderItems((prev) => ({
      ...prev,
      [productId]: quantity > 0 ? quantity : undefined,
    }));
  };

  const handleBackClick = () => {
    history.push('/storage');
  };

  const handleSubmitOrder = async () => {
    // Walidacja
    const validation = validateOrderData(selectedProducer, orderItems);

    if (!validation.isValid) {
      validation.errors.forEach((error) => alert.error(error));
      return;
    }

    try {
      setIsSubmitting(true);
      dispatch(setSpinner(true));

      await createStorageOrder({
        producerName: selectedProducer,
        items: validation.itemsToOrder,
      });

      alert.success('Zamówienie zostało utworzone pomyślnie!');
      // Ustaw aktywną zakładkę na zamówienia przed przekierowaniem
      dispatch(setStoragePageKey('orders'));
      history.push('/storage');
    } catch (error) {
      console.error('Error creating order:', error);
      alert.error('Błąd podczas tworzenia zamówienia');
    } finally {
      setIsSubmitting(false);
      dispatch(setSpinner(false));
    }
  };

  const calculateTotalQuantity = () => {
    return Object.values(orderItems).reduce(
      (sum, quantity) => sum + (parseInt(quantity, 10) || 0),
      0,
    );
  };

  const calculateTotalPrice = () => {
    return Object.entries(orderItems).reduce(
      (sum, [productId, quantity]) => {
        const product = availableProducts.find((p) => p._id === productId);
        if (product && quantity > 0) {
          return sum + product.price * parseInt(quantity, 10);
        }
        return sum;
      },
      0,
    );
  };

  return (
    <PageTemplate>
      <PageHeader
        title="Nowe zamówienie magazynowe"
        extra={[
          <Button
            key="back"
            variant="outline-secondary"
            onClick={handleBackClick}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Powrót do magazynu
          </Button>,
        ]}
      />

      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Formularz zamówienia</h5>
              </Card.Header>
              <Card.Body>
                <ProducerSelect
                  producers={mdfProducers}
                  selectedProducer={selectedProducer}
                  onChange={handleProducerChange}
                />

                {selectedProducer && availableProducts.length > 0 && (
                  <>
                    <ProductsTable
                      products={availableProducts}
                      orderItems={orderItems}
                      onQuantityChange={handleQuantityChange}
                    />

                    <OrderSummary
                      totalQuantity={calculateTotalQuantity()}
                      totalPrice={calculateTotalPrice()}
                    />

                    <div className="d-grid gap-2 mt-4">
                      <Button
                        variant="success"
                        size="lg"
                        onClick={handleSubmitOrder}
                        disabled={
                          isSubmitting || calculateTotalQuantity() === 0
                        }
                      >
                        {isSubmitting
                          ? 'Tworzenie zamówienia...'
                          : 'Zamów'}
                      </Button>
                    </div>
                  </>
                )}

                {selectedProducer && availableProducts.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-muted">
                      Brak produktów dla wybranego producenta:{' '}
                      <strong>{selectedProducer}</strong>
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </PageTemplate>
  );
};

export default StorageNewOrder;
